import useLanguageStore from "../data/languageStore";
import { TranslationService } from "../services/TranslationService";
import { useState, useEffect, useCallback, useRef, useMemo } from "react";

type LangPageCache = { [pageId: number]: { [textCode: string]: string } };

/** Bulk response groups strings by page; the same textCode can exist on another page — merge for lookup. */
function mergeAllPagesForLanguage(
  langCache: LangPageCache | undefined,
  currentPageId: number
): { [textCode: string]: string } {
  if (!langCache || typeof langCache !== "object") {
    return {};
  }
  const merged: { [textCode: string]: string } = {};
  for (const pageMap of Object.values(langCache)) {
    if (pageMap && typeof pageMap === "object") {
      Object.assign(merged, pageMap);
    }
  }
  const pageSpecific = langCache[currentPageId];
  if (pageSpecific && typeof pageSpecific === "object") {
    Object.assign(merged, pageSpecific);
  }
  return merged;
}

function hasBulkLanguagePages(
  translationCache: { [languageId: number]: LangPageCache },
  languageId: number
): boolean {
  const lang = translationCache[languageId];
  return !!lang && typeof lang === "object" && Object.keys(lang).length > 0;
}

interface UseTranslationOptions {
  pageId?: number;
  addressSystemId?: number;
  autoLoad?: boolean; // Load translations on mount (now uses cache first)
}

/**
 * Bulk load ALL translations for ALL languages into the global cache.
 * Call this ONCE after address system selection (e.g., in Dashboard).
 * Language switching is then instant - no additional API calls needed.
 */
export const loadAllTranslationsToCache = async (addressSystemId: number) => {
  const cacheKey = `${addressSystemId}`;
  const state = useLanguageStore.getState();
  
  // Skip if already loaded for this address system
  if (state.translationCacheKey === cacheKey && state.isTranslationsLoaded) {
    console.log(`[loadAllTranslationsToCache] Cache already loaded for addressSystemId=${addressSystemId}`);
    return;
  }

  // Skip if already loading (prevent duplicate calls)
  if (state.isTranslationsLoading) {
    console.log(`[loadAllTranslationsToCache] Already loading, skipping duplicate call`);
    return;
  }

  // Set loading state to prevent race conditions
  useLanguageStore.getState().setIsTranslationsLoading(true);

  console.log(`[loadAllTranslationsToCache] Loading ALL translations (all languages) for addressSystemId=${addressSystemId}`);
  const startTime = performance.now();

  try {
    // Fetch all languages at once
    const allTranslations = await TranslationService.getAllTranslations(addressSystemId);
    
    // Convert to cache format: { [languageId]: { [pageId]: { [textCode]: translatedText } } }
    const cache: { [languageId: number]: { [pageId: number]: { [textCode: string]: string } } } = {};
    
    let totalPages = 0;
    for (const [langIdStr, pageTranslations] of Object.entries(allTranslations)) {
      const langId = parseInt(langIdStr);
      cache[langId] = {};
      
      for (const [pageIdStr, translations] of Object.entries(pageTranslations as { [key: string]: any[] })) {
        const pageId = parseInt(pageIdStr);
        cache[langId][pageId] = {};
        totalPages++;
        
        if (Array.isArray(translations)) {
          translations.forEach((item: any) => {
            // Fallback chain: contractPageText → commonPageText → commonPageTextEng → textCode
            cache[langId][pageId][item.textCode] = item.contractPageText || item.commonPageText || item.commonPageTextEng || item.textCode;
          });
        }
      }
    }
    
    useLanguageStore.getState().setTranslationCache(cache, cacheKey);
    
    const elapsed = performance.now() - startTime;
    console.log(`[loadAllTranslationsToCache] Loaded ${Object.keys(cache).length} languages, ${totalPages} total pages in ${elapsed.toFixed(0)}ms`);
  } catch (err) {
    console.error(`[loadAllTranslationsToCache] Error:`, err);
    useLanguageStore.getState().setIsTranslationsLoading(false);
    throw err;
  }
};

export const useTranslation = (options: UseTranslationOptions = {}) => {
  const { pageId = 1, addressSystemId = 0, autoLoad = true } = options;
  
  const selectedLanguage = useLanguageStore((state: any) => state.selectedLanguage);
  const translationCache = useLanguageStore((state: any) => state.translationCache);
  const isTranslationsLoaded = useLanguageStore((state: any) => state.isTranslationsLoaded);
  const isTranslationsLoading = useLanguageStore((state: any) => state.isTranslationsLoading);
  
  // Local state for fallback API loading (only if cache miss AND bulk load failed)
  const [localTranslations, setLocalTranslations] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Cache missing keys to avoid repeated lookups (reset when page/language/address changes)
  const missingKeysRef = useRef<Set<string>>(new Set());
  /** Set after a successful per-page fetch for configKey, including when the API returns []. */
  const loadedConfigRef = useRef<string>("");
  const loadGenerationRef = useRef(0);

  useEffect(() => {
    missingKeysRef.current.clear();
    loadedConfigRef.current = "";
    loadGenerationRef.current += 1;
  }, [pageId, addressSystemId, selectedLanguage]);

  // After bulk load, merge all pages for the language so keys resolve even if stored under another pageId in DB
  const translations = useMemo(() => {
    if (!isTranslationsLoaded) {
      return localTranslations;
    }
    if (!hasBulkLanguagePages(translationCache, selectedLanguage)) {
      return localTranslations;
    }
    const langCache = translationCache[selectedLanguage] as LangPageCache;
    const merged = mergeAllPagesForLanguage(langCache, pageId);
    return Object.keys(merged).length > 0 ? merged : localTranslations;
  }, [isTranslationsLoaded, translationCache, selectedLanguage, pageId, localTranslations]);

  /**
   * Fallback: Fetch translations for this page if not in cache.
   * Called when: bulk not triggered yet, bulk failed, or page missing from bulk.
   */
  const loadPageTranslations = useCallback(async () => {
    if (!pageId) {
      console.warn("[useTranslation] pageId is required");
      return;
    }

    // Skip if bulk load is in progress - wait for it instead
    if (isTranslationsLoading) {
      return;
    }

    // Bulk already has full language payload — no per-page API needed
    if (
      isTranslationsLoaded &&
      hasBulkLanguagePages(translationCache, selectedLanguage)
    ) {
      return;
    }

    const configKey = `${pageId}-${addressSystemId}-${selectedLanguage}`;
    // Skip if we already completed a fetch for this config (empty API result is still "loaded")
    if (loadedConfigRef.current === configKey) {
      return;
    }

    console.log(`[useTranslation] Page ${pageId} loading individually (bulk loaded: ${isTranslationsLoaded})`);

    const generation = loadGenerationRef.current;

    try {
      setLoading(true);
      setError(null);

      // Fetch all translations for this page in one API call
      const result = await TranslationService.getTranslations(
        pageId,
        addressSystemId,
        selectedLanguage
      );

      if (generation !== loadGenerationRef.current) {
        return;
      }

      // Convert array of PageText to key-value object
      const translationMap: { [key: string]: string } = {};
      if (Array.isArray(result)) {
        result.forEach((item: any) => {
          // Fallback chain: contractPageText → commonPageText → commonPageTextEng → textCode
          translationMap[item.textCode] = item.contractPageText || item.commonPageText || item.commonPageTextEng || item.textCode;
        });
      }

      setLocalTranslations(translationMap);
      loadedConfigRef.current = configKey;
      if (Object.keys(translationMap).length > 0) {
        missingKeysRef.current.clear();
      }

    } catch (err) {
      if (generation === loadGenerationRef.current) {
        const errorMsg = err instanceof Error ? err.message : "Failed to load translations";
        setError(errorMsg);
        console.error(`[useTranslation] Error loading translations:`, err);
      }
    } finally {
      setLoading(false);
    }
  }, [pageId, addressSystemId, selectedLanguage, isTranslationsLoaded, isTranslationsLoading, translationCache]);

  /**
   * Auto-load translations when component mounts or dependencies change
   * Works both before and after bulk load
   */
  useEffect(() => {
    if (autoLoad && !isTranslationsLoading) {
      const inBulkCache =
        isTranslationsLoaded &&
        hasBulkLanguagePages(translationCache, selectedLanguage);
      const configKey = `${pageId}-${addressSystemId}-${selectedLanguage}`;
      const inLocalCache = loadedConfigRef.current === configKey;

      if (!inBulkCache && !inLocalCache) {
        loadPageTranslations();
      }
    }
  }, [loadPageTranslations, autoLoad, isTranslationsLoaded, isTranslationsLoading, translationCache, pageId, selectedLanguage, addressSystemId]);

  /**
   * Get a single translation by text code
   * Returns from cache (no API call needed)
   * Optimized to avoid repeated console.warn calls
   * Returns null when translation is missing to enable fallback patterns: getTranslation("KEY") || "Default"
   */
  const getTranslation = useCallback(
    (textCode: string): string | null => {
      if (!textCode) {
        return null;
      }

      if (translations[textCode]) {
        return translations[textCode];
      }

      if (missingKeysRef.current.has(textCode)) {
        return null;
      }

      // Avoid console spam while bulk or per-page fetch is still in flight
      if (isTranslationsLoading || loading) {
        return null;
      }

      console.warn(
        `[useTranslation] Translation not found for textCode: ${textCode}`
      );
      missingKeysRef.current.add(textCode);

      return null;
    },
    [translations, isTranslationsLoading, loading]
  );

  /**
   * Get multiple translations at once
   * Returns from cache (no API call needed)
   * Optimized version - no logging
   */
  const getMultipleTranslations = useCallback((textCodes: string[]): { [key: string]: string } => {
    const result: { [key: string]: string } = {};
    
    textCodes.forEach((code) => {
      result[code] = translations[code] || code;
    });
    
    return result;
  }, [translations]);

  /**
   * Manually reload translations (useful if you need to refresh)
   */
  const reloadTranslations = useCallback(async () => {
    loadedConfigRef.current = "";
    await loadPageTranslations();
  }, [loadPageTranslations]);

  return {
    selectedLanguage,
    translations,
    loading,
    error,
    getTranslation,          // Get single translation from cache
    getMultipleTranslations, // Get multiple translations from cache
    loadPageTranslations,    // Manually load all translations
    reloadTranslations,      // Reload all translations
  };
};